import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { DashboardLayout } from '../layout/DashboardLayout';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useAuth } from '../../contexts/AuthContext';

type GoalCategory = 'financial' | 'professional' | 'personal';

interface GoalRecord {
  id: string;
  category: GoalCategory;
  title: string;
}

const GOAL_CATEGORIES: Array<{ key: GoalCategory; label: string; placeholder: string }> = [
  {
    key: 'financial',
    label: 'Financial goal',
    placeholder: 'Raise new capital, grow revenue, improve margins…',
  },
  {
    key: 'professional',
    label: 'Professional goal',
    placeholder: 'Launch a product, expand team capabilities, win key accounts…',
  },
  {
    key: 'personal',
    label: 'Personal goal',
    placeholder: 'Improve wellbeing, deepen expertise, build relationships…',
  },
];

const EMPTY_GOAL_INPUTS: Record<GoalCategory, string> = {
  financial: '',
  professional: '',
  personal: '',
};

const INTEREST_CATEGORIES = [
  {
    title: 'Technology & Innovation',
    options: [
      'AI & Machine Learning',
      'Quantum Computing',
      'Cybersecurity',
      'Internet Infrastructure',
      'Semiconductors',
      'Web3 & Blockchain',
      'Biotech & Genomics',
      'Robotics',
      'Clean Tech',
    ],
  },
  {
    title: 'Geopolitics & Security',
    options: [
      'US Domestic Policy',
      'US National Security',
      'European Affairs',
      'Middle East',
      'Asia-Pacific',
      'Latin America',
      'Africa',
      'Diplomacy & Negotiation',
      'Sanctions & Export Controls',
    ],
  },
  {
    title: 'Economy & Markets',
    options: [
      'Macroeconomics',
      'Global Trade',
      'Supply Chains',
      'Capital Markets',
      'Private Equity',
      'Venture Capital',
      'Real Estate',
      'Fintech',
      'Renewable Energy Markets',
    ],
  },
  {
    title: 'Lifestyle & Personal Interests',
    options: [
      'Rock Climbing',
      'Mountain Biking',
      'Trail Running',
      'Backcountry Skiing',
      'Surfing',
      'Photography',
      'Music Production',
      'Culinary Arts',
      'Travel Planning',
    ],
  },
];

export function Profile() {
  const supabase = useSupabaseClient();
  const { getEffectiveUserId } = useAuth();
  const { profile, loading, updating, error, updateBasicInfo, updateOptionalFields } = useUserProfile();
  const [editingBasicInfo, setEditingBasicInfo] = useState(false);
  const [editingBio, setEditingBio] = useState(false);
  const [editingInterests, setEditingInterests] = useState(false);
  const [editingGoals, setEditingGoals] = useState(false);
  const [basicInfo, setBasicInfo] = useState({ fullName: '', preferredEmail: '' });
  const [basicInfoTouched, setBasicInfoTouched] = useState(false);
  const [bioValue, setBioValue] = useState('');
  const [interestsValue, setInterestsValue] = useState<string[]>([]);
  const [newInterest, setNewInterest] = useState('');
  const [goalInputs, setGoalInputs] = useState<Record<GoalCategory, string>>(EMPTY_GOAL_INPUTS);
  const [goalRecords, setGoalRecords] = useState<GoalRecord[]>([]);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [goalsSaving, setGoalsSaving] = useState(false);
  const [goalsError, setGoalsError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!profile) return;
    setBasicInfo({
      fullName: profile.full_name ?? '',
      preferredEmail: profile.preferred_email ?? '',
    });
    setBioValue(profile.bio ?? '');
    setInterestsValue(profile.interests ?? []);
  }, [profile]);

  const handleStartEditBasicInfo = useCallback(() => {
    setBasicInfo({
      fullName: profile?.full_name ?? '',
      preferredEmail: profile?.preferred_email ?? '',
    });
    setBasicInfoTouched(false);
    setEditingBasicInfo(true);
    setSaveError(null);
    setSaveSuccess(null);
  }, [profile?.full_name, profile?.preferred_email]);

  const handleCancelEditBasicInfo = useCallback(() => {
    setBasicInfo({
      fullName: profile?.full_name ?? '',
      preferredEmail: profile?.preferred_email ?? '',
    });
    setBasicInfoTouched(false);
    setEditingBasicInfo(false);
    setSaveError(null);
  }, [profile?.full_name, profile?.preferred_email]);

  const handleSaveBasicInfo = useCallback(async () => {
    setSaveError(null);
    setSaveSuccess(null);
    setBasicInfoTouched(true);
    
    const trimmedName = basicInfo.fullName.trim();
    const trimmedEmail = basicInfo.preferredEmail.trim();
    
    if (!trimmedName || !trimmedEmail) {
      setSaveError('Both name and email are required.');
      return;
    }

    const result = await updateBasicInfo({
      fullName: trimmedName,
      preferredEmail: trimmedEmail,
    });
    
    if (result) {
      setEditingBasicInfo(false);
      setSaveSuccess('Basic information updated successfully.');
      setTimeout(() => setSaveSuccess(null), 3000);
    } else {
      setSaveError('Failed to update basic information. Please try again.');
    }
  }, [basicInfo, updateBasicInfo]);

  const handleBasicInfoChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setBasicInfo(prev => ({ ...prev, [name]: value }));
    setSaveError(null);
  }, []);

  const handleStartEditBio = useCallback(() => {
    setBioValue(profile?.bio ?? '');
    setEditingBio(true);
    setSaveError(null);
    setSaveSuccess(null);
  }, [profile?.bio]);

  const handleCancelEditBio = useCallback(() => {
    setBioValue(profile?.bio ?? '');
    setEditingBio(false);
    setSaveError(null);
  }, [profile?.bio]);

  const handleSaveBio = useCallback(async () => {
    setSaveError(null);
    setSaveSuccess(null);
    const result = await updateOptionalFields({ bio: bioValue.trim() ? bioValue.trim() : null });
    if (result) {
      setEditingBio(false);
      setSaveSuccess('Bio updated successfully.');
      setTimeout(() => setSaveSuccess(null), 3000);
    } else {
      setSaveError('Failed to update bio. Please try again.');
    }
  }, [bioValue, updateOptionalFields]);

  const handleStartEditInterests = useCallback(() => {
    setInterestsValue(profile?.interests ?? []);
    setNewInterest('');
    setEditingInterests(true);
    setSaveError(null);
    setSaveSuccess(null);
  }, [profile?.interests]);

  const handleCancelEditInterests = useCallback(() => {
    setInterestsValue(profile?.interests ?? []);
    setNewInterest('');
    setEditingInterests(false);
    setSaveError(null);
  }, [profile?.interests]);

  const handleSaveInterests = useCallback(async () => {
    setSaveError(null);
    setSaveSuccess(null);
    const trimmedInterests = Array.from(new Set(interestsValue.map(value => value.trim()).filter(Boolean)));
    const result = await updateOptionalFields({ interests: trimmedInterests });
    if (result) {
      setNewInterest('');
      setEditingInterests(false);
      setSaveSuccess('Interests updated successfully.');
      setTimeout(() => setSaveSuccess(null), 3000);
    } else {
      setSaveError('Failed to update interests. Please try again.');
    }
  }, [interestsValue, updateOptionalFields]);

  const toggleInterest = useCallback((value: string) => {
    setInterestsValue(prev => {
      const trimmed = value.trim();
      if (!trimmed) return prev;
      if (prev.some(item => item.toLowerCase() === trimmed.toLowerCase())) {
        return prev.filter(item => item.toLowerCase() !== trimmed.toLowerCase());
      }
      return [...prev, trimmed];
    });
  }, []);

  const handleNewInterestSubmit = useCallback(() => {
    const trimmed = newInterest.trim();
    if (!trimmed) {
      return;
    }
    toggleInterest(trimmed);
    setNewInterest('');
  }, [newInterest, toggleInterest]);

  const fetchGoals = useCallback(async () => {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      setGoalRecords([]);
      setGoalInputs(EMPTY_GOAL_INPUTS);
      return;
    }

    setGoalsLoading(true);
    setGoalsError(null);
    try {
      const { data, error } = await supabase
        .from('goals')
        .select('id, category, title')
        .eq('user_id', effectiveUserId)
        .order('category', { ascending: true });

      if (error) throw error;

      const records = (data ?? []) as GoalRecord[];
      setGoalRecords(records);
      const nextInputs: Record<GoalCategory, string> = { ...EMPTY_GOAL_INPUTS };
      records.forEach(goal => {
        nextInputs[goal.category] = goal.title ?? '';
      });
      setGoalInputs(nextInputs);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load goals.';
      setGoalsError(message);
    } finally {
      setGoalsLoading(false);
    }
  }, [supabase, getEffectiveUserId]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const handleStartEditGoals = useCallback(() => {
    fetchGoals();
    setEditingGoals(true);
    setSaveError(null);
    setSaveSuccess(null);
  }, [fetchGoals]);

  const handleCancelEditGoals = useCallback(() => {
    fetchGoals();
    setEditingGoals(false);
    setSaveError(null);
  }, [fetchGoals]);

  const handleGoalInputChange = useCallback((category: GoalCategory, value: string) => {
    setGoalInputs(prev => ({ ...prev, [category]: value }));
    setSaveError(null);
  }, []);

  const handleSaveGoals = useCallback(async () => {
    setSaveError(null);
    setSaveSuccess(null);
    setGoalsSaving(true);
    setGoalsError(null);

    try {
      const trimmedInputs: Record<GoalCategory, string> = {
        financial: goalInputs.financial.trim(),
        professional: goalInputs.professional.trim(),
        personal: goalInputs.personal.trim(),
      };

      const effectiveUserId = getEffectiveUserId();
      if (!effectiveUserId) {
        throw new Error('User must be authenticated');
      }

      for (const { key } of GOAL_CATEGORIES) {
        const existing = goalRecords.find(goal => goal.category === key);
        const text = trimmedInputs[key];

        if (existing && !text) {
          const { error } = await supabase.from('goals').delete().eq('id', existing.id);
          if (error) throw error;
        } else if (existing && text && text !== existing.title) {
          const { error } = await supabase.from('goals').update({ title: text }).eq('id', existing.id);
          if (error) throw error;
        } else if (!existing && text) {
          const { error } = await supabase.from('goals').insert({
            title: text,
            category: key,
            user_id: effectiveUserId,
          });
          if (error) throw error;
        }
      }

      await fetchGoals();
      const savedTitles = GOAL_CATEGORIES.map(({ key }) => trimmedInputs[key]).filter(Boolean);
      await updateOptionalFields({ goals: savedTitles });

      setEditingGoals(false);
      setSaveSuccess('Goals updated successfully.');
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to update goals. Please try again.');
    } finally {
      setGoalsSaving(false);
    }
  }, [fetchGoals, goalInputs, goalRecords, updateOptionalFields, supabase, getEffectiveUserId]);

  if (loading) {
    return (
      <DashboardLayout title="Profile">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (!profile) {
    return (
      <DashboardLayout title="Profile">
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Failed to load profile.</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Profile">
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Your Profile
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Manage your profile information, goals, bio, and interests to personalize your experience.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => {}}>
            {error}
          </Alert>
        )}

        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}

        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveSuccess(null)}>
            {saveSuccess}
          </Alert>
        )}

        {/* Basic Information Section */}
        <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Basic Information
            </Typography>
            {!editingBasicInfo && (
              <Button
                startIcon={<EditIcon />}
                onClick={handleStartEditBasicInfo}
                variant="outlined"
                size="small"
              >
                Edit
              </Button>
            )}
          </Box>
          {editingBasicInfo ? (
            <Box>
              <TextField
                label="Full name"
                name="fullName"
                value={basicInfo.fullName}
                onChange={handleBasicInfoChange}
                fullWidth
                autoComplete="name"
                error={basicInfoTouched && basicInfo.fullName.trim().length === 0}
                helperText={basicInfoTouched && basicInfo.fullName.trim().length === 0 ? 'Name is required.' : ''}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Preferred email"
                name="preferredEmail"
                value={basicInfo.preferredEmail}
                onChange={handleBasicInfoChange}
                fullWidth
                autoComplete="email"
                error={basicInfoTouched && basicInfo.preferredEmail.trim().length === 0}
                helperText={basicInfoTouched && basicInfo.preferredEmail.trim().length === 0 ? 'Email is required.' : ''}
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                We use this information to personalize your workspace experience and notify you of important activity.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveBasicInfo}
                  disabled={updating}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelEditBasicInfo}
                  disabled={updating}
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Name:</strong> {profile.full_name || 'Not set'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Email:</strong> {profile.preferred_email || 'Not set'}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Bio Section */}
        <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Bio
            </Typography>
            {!editingBio && (
              <Button
                startIcon={<EditIcon />}
                onClick={handleStartEditBio}
                variant="outlined"
                size="small"
              >
                Edit
              </Button>
            )}
          </Box>
          {editingBio ? (
            <Box>
              <TextField
                label="Bio"
                value={bioValue}
                onChange={event => setBioValue(event.target.value)}
                fullWidth
                multiline
                minRows={4}
                helperText="A short description helps others understand your work."
                sx={{ mb: 2 }}
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveBio}
                  disabled={updating}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelEditBio}
                  disabled={updating}
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          ) : (
            <Typography variant="body1" color={profile.bio ? 'text.primary' : 'text.secondary'}>
              {profile.bio || 'No bio added yet. Click Edit to add one.'}
            </Typography>
          )}
        </Paper>

        {/* Interests Section */}
        <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Interests
            </Typography>
            {!editingInterests && (
              <Button
                startIcon={<EditIcon />}
                onClick={handleStartEditInterests}
                variant="outlined"
                size="small"
              >
                Edit
              </Button>
            )}
          </Box>
          {editingInterests ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Select interests from the categories below or add your own. Click on selected interests to remove them.
              </Typography>
              <Stack spacing={3} sx={{ mb: 3 }}>
                {INTEREST_CATEGORIES.map(category => (
                  <Box key={category.title}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                      {category.title}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {category.options.map(option => {
                        const selected = interestsValue.some(item => item.toLowerCase() === option.toLowerCase());
                        return (
                          <Chip
                            key={option}
                            label={option}
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            onClick={() => toggleInterest(option)}
                            sx={{ mb: 1 }}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                ))}
              </Stack>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                <TextField
                  label="Add custom interest"
                  value={newInterest}
                  onChange={event => setNewInterest(event.target.value)}
                  onKeyPress={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleNewInterestSubmit();
                    }
                  }}
                  sx={{ flexGrow: 1, minWidth: 220 }}
                />
                <Button variant="outlined" onClick={handleNewInterestSubmit}>
                  Add
                </Button>
              </Box>
              {interestsValue.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Selected interests:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {interestsValue.map(interest => (
                      <Chip
                        key={interest}
                        label={interest}
                        color="primary"
                        onDelete={() => toggleInterest(interest)}
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveInterests}
                  disabled={updating}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelEditInterests}
                  disabled={updating}
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box>
              {profile.interests && profile.interests.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {profile.interests.map(interest => (
                    <Chip key={interest} label={interest} color="primary" sx={{ mb: 1 }} />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No interests added yet. Click Edit to add some.
                </Typography>
              )}
            </Box>
          )}
        </Paper>

        {/* Goals Section */}
        <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Goals
            </Typography>
            {!editingGoals && (
              <Button
                startIcon={<EditIcon />}
                onClick={handleStartEditGoals}
                variant="outlined"
                size="small"
              >
                Edit
              </Button>
            )}
          </Box>
          {editingGoals ? (
            <Box>
              {goalsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {goalsError}
                </Alert>
              )}
              {goalsLoading && goalRecords.length === 0 ? (
                <Box sx={{ py: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <>
                  <Stack spacing={3} sx={{ mb: 3 }}>
                    {GOAL_CATEGORIES.map(({ key, label, placeholder }) => (
                      <Box key={key}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {label}
                        </Typography>
                        <TextField
                          value={goalInputs[key]}
                          onChange={event => handleGoalInputChange(key, event.target.value)}
                          placeholder={placeholder}
                          fullWidth
                          multiline
                          minRows={2}
                        />
                      </Box>
                    ))}
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveGoals}
                      disabled={updating || goalsSaving}
                    >
                      {goalsSaving ? 'Saving…' : 'Save'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleCancelEditGoals}
                      disabled={updating || goalsSaving}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </>
              )}
            </Box>
          ) : (
            <Box>
              {goalRecords.length > 0 ? (
                <Stack spacing={2}>
                  {GOAL_CATEGORIES.map(({ key, label }) => {
                    const goal = goalRecords.find(g => g.category === key);
                    if (!goal || !goal.title) return null;
                    return (
                      <Box key={key}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {label}
                        </Typography>
                        <Typography variant="body1" color="text.primary">
                          {goal.title}
                        </Typography>
                      </Box>
                    );
                  })}
                  {goalRecords.length === 0 && (
                    <Typography variant="body1" color="text.secondary">
                      No goals added yet. Click Edit to add some.
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No goals added yet. Click Edit to add some.
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      </Box>
    </DashboardLayout>
  );
}

